import { useSyncExternalStore } from 'react';
import type { Catalogue } from './types';

// Everything the page remembers lives in localStorage under the shop's slug:
// the signed session (so a reload keeps a signed-in customer), the last
// catalogue with its ETag (so a revisit costs one 304), and the cart. Nothing
// here is trusted by the server — the session is signed, and the cart is
// re-priced at checkout (M2).

export interface StoredSession {
  value: string;
  contactId: number | null;
  contactName: string | null;
  groupId: number;
  pricesShown: boolean;
  expiresAt: string;
}

export interface CartLine {
  productId: number;
  variantId: number;
  qty: number;
}

export interface StoredCatalogue {
  etag: string;
  catalogue: Catalogue;
}

const PREFIX = 'wa-shop';

function read<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function write(key: string, value: unknown): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Private mode or a full quota: the page still works, it just forgets.
  }
}

export const sessionStore = {
  key: (slug: string) => `${PREFIX}:session:${slug}`,
  get(slug: string): StoredSession | null {
    const s = read<StoredSession>(this.key(slug));
    if (!s) return null;
    if (Date.parse(s.expiresAt) <= Date.now()) {
      write(this.key(slug), null);
      return null;
    }
    return s;
  },
  set(slug: string, s: StoredSession | null) {
    write(this.key(slug), s);
  },
};

export const catalogueStore = {
  key: (slug: string, groupId: number) => `${PREFIX}:catalogue:${slug}:${groupId}`,
  get(slug: string, groupId: number): StoredCatalogue | null {
    return read<StoredCatalogue>(this.key(slug, groupId));
  },
  set(slug: string, groupId: number, c: StoredCatalogue | null) {
    write(this.key(slug, groupId), c);
  },
};

// The cart is the one piece of state React renders from, so it is an external
// store with subscriptions rather than something re-read on every render.
const cartListeners = new Set<() => void>();
const cartCache = new Map<string, CartLine[]>();
const EMPTY: CartLine[] = [];

function cartKey(slug: string) {
  return `${PREFIX}:cart:${slug}`;
}

function loadCart(slug: string): CartLine[] {
  let lines = cartCache.get(slug);
  if (!lines) {
    lines = read<CartLine[]>(cartKey(slug)) ?? EMPTY;
    cartCache.set(slug, lines);
  }
  return lines;
}

function saveCart(slug: string, lines: CartLine[]) {
  cartCache.set(slug, lines);
  write(cartKey(slug), lines.length ? lines : null);
  for (const l of cartListeners) l();
}

export const cartStore = {
  subscribe(listener: () => void) {
    cartListeners.add(listener);
    return () => {
      cartListeners.delete(listener);
    };
  },
  get: loadCart,
  setQty(slug: string, productId: number, variantId: number, qty: number) {
    const lines = loadCart(slug).filter((l) => l.variantId !== variantId);
    if (qty > 0) lines.push({ productId, variantId, qty: Math.min(999, Math.floor(qty)) });
    saveCart(slug, lines);
  },
  add(slug: string, productId: number, variantId: number, qty: number) {
    const current = loadCart(slug).find((l) => l.variantId === variantId)?.qty ?? 0;
    this.setQty(slug, productId, variantId, current + qty);
  },
  clear(slug: string) {
    saveCart(slug, []);
  },
};

export function useCart(slug: string): CartLine[] {
  return useSyncExternalStore(cartStore.subscribe, () => loadCart(slug), () => EMPTY);
}
